import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { GraduationCapIcon, ShieldCheckIcon, MailIcon, ArrowRightIcon, CheckIcon, LockIcon, ZapIcon, ClipboardListIcon, SparklesIcon, SchoolIcon, AwardIcon } from 'lucide-react';
import { getBridgeTemplate } from '../data/bridgeTemplates';
import { useMetaTags } from '../hooks/useMetaTags';
import { V9BridgeLayout } from '../components/V9BridgeLayout';
import { getPublisherParams } from '../utils/publisherParams';
import { degreeLevelOptions, areaStudyOptions, programOptions, getDefaultProgram } from '../data/filterOptions';
const NAVY = '#1a2332';
const NAVY_ACCENT = '#2a374a';
const ORANGE = '#e8594f';
const ORANGE_HOVER = '#d64a41';
// Module-level lock so the email-remarketing beacon fires at most once per
// unique lead for the lifetime of the page session. A component-scoped ref
// resets on every remount (React StrictMode double-mount, route remounts,
// HMR), which caused the beacon to fire repeatedly. A module-level Set keyed
// by the request URL survives all remounts while still allowing a genuinely
// different lead (different URL) to fire its own single beacon.
const firedRemarketingUrls = new Set<string>();
export function FindDegreesBridgePage() {
  const {
    templateId
  } = useParams<{
    templateId: string;
  }>();
  const template = getBridgeTemplate(templateId);
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // v6 selector state
  const [degreeLevel, setDegreeLevel] = useState<number | ''>('');
  const [areaOfStudy, setAreaOfStudy] = useState<number | ''>('');
  const [program, setProgram] = useState<number | ''>('');
  // v8 — Clicks Search carousel state. When `showClicksCarousel` is
  // true, the page swaps the selector form for an in-page carousel
  // that displays one school at a time from the Clicks API response.
  const [showClicksCarousel, setShowClicksCarousel] = useState(false);
  const [clicksResults, setClicksResults] = useState<any[]>([]);
  const [clicksLoading, setClicksLoading] = useState(false);
  const [clicksIndex, setClicksIndex] = useState(0);
  const [clicksError, setClicksError] = useState('');
  useMetaTags({
    title: `${template.headlineHighlight} | FindOnlineDegrees`,
    description: template.subhead,
    // Bridge templates are paid traffic landing pages — keep them out of
    // the organic index so they don't compete with content pages.
    robots: 'noindex, follow'
  });
  // Email remarketing beacon — fires once on landing whenever the URL
  // carries an email AND a first and/or last name. Forwards the lead to
  // Media Matchers' email-remarketing endpoint so the contact can be
  // enrolled in downstream nurture campaigns. Fire-and-forget (no UI).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const email = (params.get('email') || '').trim();
    const firstName = (params.get('firstName') || '').trim();
    const lastName = (params.get('lastName') || '').trim();
    // Require an email AND at least one name part before firing.
    if (!email || !firstName && !lastName) return;
    const remarketingParams = new URLSearchParams();
    // Publisher identification: prefer mPubGroupID, else mPubID+mPubCampaignID
    // from the URL; if all are blank fall back to the partner-supplied
    // mPubID/mPubCampaignID default pair.
    Object.entries(getPublisherParams(location.search, {
      mPubID: '83768',
      mPubCampaignID: '37138'
    })).forEach(([k, v]) => remarketingParams.set(k, v));
    // Map remaining values straight from the URL — only append the ones
    // that actually have a value so we don't send empty params.
    const passthrough: Record<string, string> = {
      firstName,
      lastName,
      email,
      sub1: params.get('sub1') || '',
      sub2: params.get('sub2') || '',
      sub3: params.get('sub3') || '',
      userIP: params.get('userIP') || '',
      zipCode: params.get('zipCode') || '',
      mProgram: params.get('mProgram') || '',
      startTimeframe: params.get('startTimeframe') || ''
    };
    Object.entries(passthrough).forEach(([k, v]) => {
      if (v) remarketingParams.set(k, v);
    });
    const url = `https://data.media-matchers.com/email-remarketing?${remarketingParams.toString()}`;
    // Guard against duplicate fires across remounts: only one beacon per
    // unique lead URL for the lifetime of this page session.
    if (firedRemarketingUrls.has(url)) return;
    firedRemarketingUrls.add(url);
    console.log('[Email Remarketing] PING (GET):', url);
    // Use default `cors` mode (not no-cors) so the response body is
    // readable. The request still reaches the server either way; cors mode
    // additionally exposes the JSON to JS/DevTools IF the endpoint returns
    // Access-Control-Allow-Origin. If it doesn't, the read is blocked
    // (the ping still lands) — open the logged URL in a browser tab to see
    // the raw JSON directly.
    fetch(url, {
      method: 'GET',
      keepalive: true
    }).then(async res => {
      const text = await res.text();
      try {
        const json = JSON.parse(text);
        console.log('[Email Remarketing] response JSON:', json);
      } catch {
        console.log('[Email Remarketing] response (raw):', text);
      }
    }).catch(err => {
      console.warn('[Email Remarketing] response not readable (likely CORS). The ping still reached the server. Open the PING URL in a new tab to see the JSON. Error:', err);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    const params = new URLSearchParams(location.search);
    params.set('email', email);
    navigate(`/?${params.toString()}`);
  };
  const handleCtaClick = () => {
    setSubmitting(true);
    const params = new URLSearchParams(location.search);
    // healthcare-v1: same single-button CTA layout as v2, but on click
    // swap the page for the in-page Clicks carousel with degreeInterest=5
    // (Master's) and mProgram=500 (Healthcare) hardcoded.
    if (templateId === 'healthcare-v1') {
      setDegreeLevel(5);
      setProgram(500);
      setShowClicksCarousel(true);
      callV8ClicksAPI(5, 500);
      return;
    }
    // v2: open the Media Matchers API as a pop-under and keep the user on
    // the listings page in the foreground tab.
    if (templateId === 'v2') {
      const externalParams = new URLSearchParams({
        sub1: params.get('sub1') || '',
        sub2: params.get('sub2') || '',
        sub3: params.get('sub3') || '',
        mediaType: '2',
        redirect: '1'
      });
      Object.entries(getPublisherParams(location.search, {
        mPubGroupID: 'PCGP4731148320'
      })).forEach(([k, v]) => externalParams.set(k, v));
      const externalUrl = `https://api.media-matchers.com/search?${externalParams.toString()}`;
      try {
        const popunder = window.open(externalUrl, '_blank');
        if (popunder) {
          popunder.blur();
          window.focus();
        }
      } catch {
        // Popup blocked or failed — proceed with main navigation regardless.
      }
      navigate(`/?${params.toString()}`);
      return;
    }
    // v5: navigate to the colleges page (preserve any sub params)
    if (templateId === 'v5') {
      navigate(`/colleges?${params.toString()}`);
      return;
    }
    // v3 only: redirect to findonlinedegrees.org request-information page
    if (templateId === 'v3') {
      const externalParams = new URLSearchParams({
        sub1: params.get('sub1') || '',
        sub2: params.get('sub2') || '',
        sub3: params.get('sub3') || ''
      });
      Object.entries(getPublisherParams(location.search, {
        mPubGroupID: 'PCGP4731148320'
      })).forEach(([k, v]) => externalParams.set(k, v));
      window.location.href = `https://findonlinedegrees.org/request-information?${externalParams.toString()}`;
      return;
    }
    navigate(`/?${params.toString()}`);
  };
  const isCta = template.variant === 'cta';
  const isSelector = template.variant === 'selector';
  const availablePrograms = areaOfStudy ? programOptions[areaOfStudy as number] || [] : [];
  // Fetch Clicks Search API and render results inline as carousel.
  // Used by both v8 (selector form) and healthcare-v1 (single CTA).
  // Accepts explicit overrides because state setters are async — the
  // caller may have just set state in the same tick.
  const callV8ClicksAPI = async (degreeInterestOverride?: number | '', mProgramOverride?: number | '') => {
    setClicksLoading(true);
    setClicksError('');
    setClicksResults([]);
    const di = degreeInterestOverride !== undefined ? degreeInterestOverride : degreeLevel;
    const mp = mProgramOverride !== undefined ? mProgramOverride : program;
    const params = new URLSearchParams(location.search);
    const searchParams = new URLSearchParams({
      degreeInterest: di === '' ? '' : String(di),
      mProgram: mp === '' ? '' : String(mp),
      sub1: params.get('sub1') || '',
      sub2: params.get('sub2') || '',
      sub3: params.get('sub3') || ''
    });
    Object.entries(getPublisherParams(location.search, {
      mPubGroupID: 'PCGP4731148320'
    })).forEach(([k, v]) => searchParams.set(k, v));
    const url = `https://api.media-matchers.com/search?${searchParams.toString()}`;
    console.log('[v8 Clicks API] PING (GET):', url);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    try {
      const response = await fetch(url, {
        signal: controller.signal
      });
      clearTimeout(timeout);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data: any = await response.json();
      console.log('[v8 Clicks API] response:', data);
      const schools = data?.result || data?.results || data?.schools || data?.data || (Array.isArray(data) ? data : []) || [];
      setClicksResults(Array.isArray(schools) ? schools : []);
      if (!schools || schools.length === 0) {
        setClicksError('No matching schools found.');
      }
    } catch (err) {
      clearTimeout(timeout);
      console.error('[v8 Clicks API] error:', err);
      setClicksError('Unable to load matching schools. Please try again.');
    } finally {
      setClicksLoading(false);
    }
  };
  const handleV8ClickThrough = () => {
    const school: any = clicksResults[clicksIndex];
    const clickURL = school?.clickURL || school?.clickUrl || school?.url || school?.link;
    if (clickURL) {
      try {
        window.open(clickURL, '_blank', 'noopener,noreferrer');
      } catch (err) {
        console.warn('[v8 Clicks] failed to open clickURL:', err);
      }
    }
    if (clicksIndex < clicksResults.length - 1) {
      setClicksIndex(clicksIndex + 1);
    } else {
      const params = new URLSearchParams(location.search);
      params.set('degreeInterest', String(degreeLevel));
      params.set('mProgram', String(program));
      navigate(`/?${params.toString()}`);
    }
  };
  const handleV8Skip = () => {
    if (clicksIndex < clicksResults.length - 1) {
      setClicksIndex(clicksIndex + 1);
    } else {
      const params = new URLSearchParams(location.search);
      params.set('degreeInterest', String(degreeLevel));
      params.set('mProgram', String(program));
      navigate(`/?${params.toString()}`);
    }
  };
  const handleSelectorSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!degreeLevel || !areaOfStudy || !program) return;
    setSubmitting(true);
    const params = new URLSearchParams(location.search);
    params.set('degreeInterest', String(degreeLevel));
    params.set('mProgram', String(program));
    // v8 and financial-aid-v1/v2: swap to in-page Clicks API carousel.
    if (templateId === 'v8' || templateId === 'financial-aid-v1' || templateId === 'financial-aid-v2') {
      setShowClicksCarousel(true);
      callV8ClicksAPI();
      return;
    }
    // v7: redirect to external Media Matchers search API,
    // and also open the home page (with the same selections) as a pop-under.
    if (templateId === 'v7') {
      const externalParams = new URLSearchParams({
        degreeInterest: String(degreeLevel),
        mProgram: String(program),
        sub1: params.get('sub1') || '',
        sub2: params.get('sub2') || '',
        sub3: params.get('sub3') || '',
        redirect: '1'
      });
      Object.entries(getPublisherParams(location.search, {
        mPubGroupID: 'PCGP4731148320'
      })).forEach(([k, v]) => externalParams.set(k, v));
      const externalUrl = `https://api.media-matchers.com/search?${externalParams.toString()}`;
      // Pop-under: open the API URL in a new tab and drop it behind,
      // then navigate the current window to the home/listings page so the
      // user stays on our site.
      try {
        const popunder = window.open(externalUrl, '_blank');
        if (popunder) {
          popunder.blur();
          window.focus();
        }
      } catch {
        // Popup blocked or failed — proceed with main navigation regardless.
      }
      navigate(`/?${params.toString()}`);
      return;
    }
    navigate(`/?${params.toString()}`);
  };
  // v9 layout override
  if (template.layout === 'hq') {
    return <V9BridgeLayout template={template} onCtaClick={handleCtaClick} submitting={submitting} data-id="element-1099" data-mp="nsmr914zbd49" />;
  }
  // v8: when the carousel is active, render it in place of the
  // selector page. Keeps the slim header for brand consistency.
  if (showClicksCarousel) {
    const currentClick: any = clicksResults[clicksIndex];
    const cSchoolName = currentClick?.mBrandName || currentClick?.brandName || currentClick?.name || 'Partner School';
    const cSchoolLogo = currentClick?.imageURL || currentClick?.imageUrl || currentClick?.logoUrl;
    const cHeadline = currentClick?.headContent || '';
    const cSchoolHighlight = currentClick?.schoolHighlight || '';
    const cAboutText = currentClick?.aboutBrand || currentClick?.aboutCollege || currentClick?.shortDescription || '';
    const cAccreditation = currentClick?.accreditation || '';
    const cRichHtml = currentClick?.bodyContent || '';
    const cClickURL = currentClick?.clickURL || currentClick?.clickUrl || currentClick?.url || currentClick?.link;
    return <div className="min-h-screen flex flex-col font-sans text-gray-900 bg-gray-50" data-id="element-1100" data-mp="218z1ktd72sfc">
        <header className="w-full relative overflow-hidden" style={{
        backgroundColor: NAVY
      }} data-id="element-1101" data-mp="25aap8tcid3kw">
          <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#2a374a] to-transparent opacity-50 pointer-events-none" data-id="element-1102" data-mp="3pt4u8dqry25w"></div>
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between relative z-10" data-id="element-1103" data-mp="3cquj1mm8sut">
            <div className="flex items-center gap-2.5" data-id="element-1104" data-mp="3urogygd6zhzb">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{
              backgroundColor: ORANGE
            }} data-id="element-1105" data-mp="3upv58mnq27kg">
                <GraduationCapIcon className="w-5 h-5 text-white" data-id="element-1106" data-mp="8bk8sw9q8nw1" />
              </div>
              <div className="leading-tight" data-id="element-1107" data-mp="mb33am9m80pv">
                <div className="font-bold text-white text-base" data-id="element-1108" data-mp="hcib9a4ionci">
                  FindOnlineDegrees
                </div>
                <div className="text-[10px] text-gray-400 tracking-widest" data-id="element-1109" data-mp="2o26fvh32fpd2">
                  YOUR MATCHES
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1.5 text-gray-300 text-xs" data-id="element-1110" data-mp="2j697py1yhpbd">
              <LockIcon className="w-3.5 h-3.5 text-white" data-id="element-1111" data-mp="n2l4ljfshgvr" />
              <span data-id="element-1112" data-mp="s0wbblasfb9r">SSL Secured</span>
            </div>
          </div>
        </header>
        <main className="flex-grow" data-id="element-1113" data-mp="wyemlica22kx">
          <div className="max-w-3xl mx-auto px-4 py-12" data-id="element-1114" data-mp="wxd10jxmxxoy">
            {clicksLoading ? <div className="text-center py-20" data-id="element-1115" data-mp="111b1oz2lzgk4">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#e8594f] mb-4" data-id="element-1116" data-mp="15zvzysyavq5x"></div>
                <p className="text-xl text-gray-700 font-semibold" data-id="element-1117" data-mp="1axxj1dvq7phe">
                  Finding your matches...
                </p>
              </div> : clicksError || clicksResults.length === 0 ? <div className="text-center py-20 bg-white rounded-xl border border-gray-200 shadow-sm" data-id="element-1118" data-mp="1knh3gnz37red">
                <p className="text-xl text-gray-700 font-semibold mb-4" data-id="element-1119" data-mp="1or5dref3t13b">
                  {clicksError || 'No matches found.'}
                </p>
                <button onClick={() => {
              const params = new URLSearchParams(location.search);
              params.set('degreeInterest', String(degreeLevel));
              params.set('mProgram', String(program));
              navigate(`/?${params.toString()}`);
            }} className="px-6 py-3 bg-[#e8594f] hover:bg-[#d64a41] text-white font-bold rounded-lg transition-all" data-id="element-1120" data-mp="1tmugfqmf6y3u">
                  Continue to Listings →
                </button>
              </div> : <>
                <div className="text-center mb-8" data-id="element-1121" data-mp="3syhvggx6vkm4">
                  <p className="text-xs uppercase tracking-widest text-[#e8594f] font-bold mb-1" data-id="element-1122" data-mp="3o4dp9zvqzgm3">
                    Your Matches
                  </p>
                  <p className="text-sm text-gray-600 font-semibold" data-id="element-1123" data-mp="kgbxdq10i0sj">
                    School {clicksIndex + 1} of {clicksResults.length}
                  </p>
                  <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden max-w-xs mx-auto" data-id="element-1124" data-mp="fo10yx5hcx4q">
                    <div className="h-full bg-[#e8594f] transition-all duration-300" style={{
                  width: `${(clicksIndex + 1) / clicksResults.length * 100}%`
                }} data-id="element-1125" data-mp="3k63uk8liyka6" />
                  </div>
                </div>
                <div className="relative bg-white rounded-xl shadow-sm border border-gray-200 p-8 md:p-10" data-id="element-1126" data-mp="3f690xxad7glf">
                  {cSchoolHighlight && <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-100 text-amber-900 text-xs font-semibold border border-amber-200 shadow-sm" data-id="element-1127" data-mp="bqk57knz5m0e">
                      <span aria-hidden="true" data-id="element-1128" data-mp="1se6fbxgsvzr">✨</span>
                      {cSchoolHighlight}
                    </span>}
                  <div className="text-center mb-8" data-id="element-1129" data-mp="2ik5tqfgxdjlw">
                    {cSchoolLogo && <img src={cSchoolLogo} alt={cSchoolName} className="h-28 md:h-32 mx-auto mb-6 object-contain" data-id="element-1130" data-mp="2mhjz1w1hj78m" />}
                    {cAboutText && <p className="text-base text-gray-600 leading-relaxed max-w-xl mx-auto" data-id="element-1131" data-mp="2ox0b25k33b4k">
                        {cAboutText}
                      </p>}
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-2" data-id="element-1132" data-mp="1wafk367it11j">
                    {cSchoolName}
                  </h2>
                  {cHeadline && cHeadline.trim() !== cSchoolName.trim() && <p className="text-base md:text-lg text-[#e8594f] font-semibold text-center mb-3" data-id="element-1133" data-mp="261a6cakro0mz">
                      {cHeadline}
                    </p>}
                  {cAccreditation && <p className="text-xs text-gray-500 text-center mb-5" data-id="element-1134" data-mp="18kbeitarur7w">
                      Accredited by {cAccreditation}
                    </p>}
                  {cRichHtml && <div className="prose prose-sm max-w-none text-gray-700 mb-6 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mb-1" dangerouslySetInnerHTML={{
                __html: cRichHtml
              }} data-id="element-1135" data-mp="2lsmatqsgp57h" />}
                  <div className="space-y-4" data-id="element-1136" data-mp="39gdk0liph24j">
                    <button onClick={handleV8ClickThrough} disabled={!cClickURL} className="w-full py-4 bg-[#e8594f] hover:bg-[#d64a41] text-white font-bold rounded-lg text-lg transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1137" data-mp="34ikg5z4vkk7f">
                      Visit {cSchoolName} →
                    </button>
                    <button onClick={handleV8Skip} className="w-full py-3 bg-white hover:bg-gray-50 text-gray-700 font-semibold rounded-lg border-2 border-gray-300 transition-all" data-id="element-1138" data-mp="1k3iiycvoasku">
                      Skip This School
                    </button>
                  </div>
                </div>
              </>}
          </div>
        </main>
        {template.footerDisclosure && <footer className="w-full border-t border-gray-200 bg-white mt-auto" data-id="element-1139" data-mp="155qt59i34ekw">
            <div className="max-w-7xl mx-auto px-4 py-8 text-center text-gray-500" data-id="element-1140" data-mp="q9hykl952pet">
              <nav className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs mb-4 font-medium" data-id="element-1141" data-mp="vbh1xv4vpmbr">
                <a href="/ca-notice" className="hover:text-[#e8594f] transition-colors" data-id="element-1142" data-mp="hbzb7a80vyzr">
                  CA Consumers: Do Not Sell My Info
                </a>
                <a href="/terms" className="hover:text-[#e8594f] transition-colors" data-id="element-1143" data-mp="2bmb9nhzsttz2">
                  Terms
                </a>
                <a href="/privacy-policy" className="hover:text-[#e8594f] transition-colors" data-id="element-1144" data-mp="2e9wumy1nd3jo">
                  Privacy Policy
                </a>
              </nav>
              <p className="text-xs mb-4" data-id="element-1145" data-mp="259qhxedrw7lk">
                © {new Date().getFullYear()} FindOnlineDegrees. All rights
                reserved.
              </p>
              <p className="text-[10px] leading-relaxed max-w-5xl mx-auto text-justify" data-id="element-1146" data-mp="2xv9q1n32wqwb">
                {template.footerDisclosure}
              </p>
            </div>
          </footer>}
      </div>;
  }
  return <div className="min-h-screen flex flex-col font-sans text-gray-900 bg-gray-100 bg-cover bg-center bg-no-repeat bg-fixed" style={{
    backgroundImage: 'url("https://cdn.magicpatterns.com/uploads/3azvozn9iRJDmqfhLE2fmB/Background_hero.png")'
  }} data-id="element-1147" data-mp="2o4op3szxnaa8">
      {/* Slim Header — matches site nav style. Hidden on mobile to maximize
                                                above-the-fold form visibility. */}
      <header className="w-full relative overflow-hidden" style={{
      backgroundColor: NAVY
    }} data-id="element-1148" data-mp="3o5s8johg1ccf">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#2a374a] to-transparent opacity-50 pointer-events-none" data-id="element-1149" data-mp="3t4d77jh1rnyr"></div>
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between relative z-10" data-id="element-1150" data-mp="618jtg1hbgnx">
          <div className="flex items-center gap-2.5" data-id="element-1151" data-mp="azjprfonl70v">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{
            backgroundColor: ORANGE
          }} data-id="element-1152" data-mp="ay8k1abndp74">
              <GraduationCapIcon className="w-5 h-5 text-white" data-id="element-1153" data-mp="kp3a6ps0l8xu" />
            </div>
            <div className="leading-tight" data-id="element-1154" data-mp="pq92wnlocw7d">
              <div className="font-bold text-white text-base" data-id="element-1155" data-mp="tqj9a1ddpomr">
                FindOnlineDegrees
              </div>
              <div className="text-[10px] text-gray-400 tracking-widest" data-id="element-1156" data-mp="1rf5y7n55tzwm">
                DEGREE MATCHING SERVICE
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 text-gray-300 text-xs" data-id="element-1157" data-mp="1me8jjkjcznzv">
            <LockIcon className="w-3.5 h-3.5 text-white" data-id="element-1158" data-mp="2077zfnn1zk7y" />
            <span data-id="element-1159" data-mp="1w5mfjic3q5o9">SSL Secured</span>
          </div>
        </div>
      </header>

      {/* Main Content — Two-Column, vertically centered */}
      <section className="flex-grow flex items-center" data-id="element-1160" data-mp="18h3gzuiut79d">
        <div className="w-full max-w-7xl mx-auto px-4 py-3 md:py-16 lg:py-20" data-id="element-1161" data-mp="13i0b67v1ux0c">
          {/* Mobile-only eyebrow + H1 — sits ABOVE the form so the user
           reads the value prop, then immediately sees the form. */}
          <div className="lg:hidden text-center mb-5" data-id="element-1162" data-mp="1ch3uncy4pefd">
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full mb-4 text-[11px] font-bold tracking-widest" style={{
            backgroundColor: '#4A49E5',
            color: '#ffffff'
          }} data-id="element-1163" data-mp="2swex6keoo5s5">
              <span className="w-1.5 h-1.5 rounded-full" style={{
              backgroundColor: '#ffffff'
            }} data-id="element-1164" data-mp="2wgfd79w2a8m6"></span>
              {template.eyebrow}
            </div>
            <h1 className="font-bold leading-[1.1] text-gray-900" style={{
            fontSize: 'clamp(1.75rem, 7vw, 2.5rem)'
          }} data-id="element-1165" data-mp="2rezp18yl7lzk">
              {template.headlineLead}{' '}
              <span style={{
              color: ORANGE
            }} data-id="element-1166" data-mp="3k2lukmrdl0p2">
                {template.headlineHighlight}
              </span>{' '}
              {template.headlineTrail}
            </h1>
          </div>

          <div className="grid lg:grid-cols-2 gap-6 md:gap-10 lg:gap-16 items-center" data-id="element-1167" data-mp="1yaefu0i3o5y">
            {/* LEFT: Headline — appears BELOW the form on mobile, beside it on desktop */}
            <div className="text-center lg:text-left order-2 lg:order-1" data-id="element-1168" data-mp="6r3isgs24uhi">
              {/* Desktop-only eyebrow + H1 (mobile renders these above the grid) */}
              <div className="hidden lg:block" data-id="element-1169" data-mp="bn7nz7do4pas">
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full mb-5 text-[11px] font-bold tracking-widest" style={{
                backgroundColor: '#4A49E5',
                color: '#ffffff'
              }} data-id="element-1170" data-mp="6mtdzjsib5a4">
                  <span className="w-1.5 h-1.5 rounded-full" style={{
                  backgroundColor: '#ffffff'
                }} data-id="element-1171" data-mp="3p0sx6iggxqx3"></span>
                  {template.eyebrow}
                </div>

                <h1 className="font-bold leading-[1.1] mb-5 text-gray-900" style={{
                fontSize: 'clamp(2rem, 4.5vw, 3.25rem)'
              }} data-id="element-1172" data-mp="3f4g33jh0hb1t">
                  {template.headlineLead}{' '}
                  <span style={{
                  color: ORANGE
                }} data-id="element-1173" data-mp="1liupe7197usv">
                    {template.headlineHighlight}
                  </span>{' '}
                  {template.headlineTrail}
                </h1>
              </div>

              {template.financialAid ? <div className="space-y-6 max-w-xl mx-auto lg:mx-0 text-left" data-id="element-1174" data-mp="1zhkvgc61x4td">
                  <div className="bg-white rounded-xl p-6 border-l-4 shadow-sm" style={{
                borderColor: ORANGE
              }} data-id="element-1175" data-mp="12uzin7427h99">
                    <p className="text-gray-600 font-medium mb-1" data-id="element-1176" data-mp="1brzzmsosud2o">
                      {template.financialAid.grantCaption.split(template.financialAid.grantAmount)[0]}
                    </p>
                    <div className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-1" data-id="element-1177" data-mp="o2p4pru4rcwg">
                      {template.financialAid.grantAmount}
                    </div>
                    <p className="text-sm text-gray-500" data-id="element-1178" data-mp="38osj7jtb435n">
                      {template.financialAid.grantCaption.split(template.financialAid.grantAmount)[1]}
                    </p>
                  </div>

                  {template.financialAid.earningsStat && <div className="bg-gray-50 rounded-xl p-5 border border-gray-200" data-id="element-1179" data-mp="3rmv17enfszty">
                      <p className="text-gray-700 text-sm md:text-base leading-relaxed mb-2" data-id="element-1180" data-mp="a5lnxuv7rkr5">
                        {template.financialAid.earningsStat}
                      </p>
                      {template.financialAid.earningsDisclaimer && <p className="text-xs text-gray-400 leading-tight" data-id="element-1181" data-mp="j6t9rdgjmt4c">
                          {template.financialAid.earningsDisclaimer}
                        </p>}
                    </div>}

                  {(template.financialAid.weCanHeadline || template.financialAid.exploreHeadline) && <div className="grid sm:grid-cols-2 gap-4" data-id="element-1182" data-mp="otbwjvmx8acm">
                      {template.financialAid.weCanHeadline && <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm" data-id="element-1183" data-mp="ssktf9d9coce">
                          <h3 className="font-bold text-gray-900 mb-2" data-id="element-1184" data-mp="f1fyuloqym8x">
                            {template.financialAid.weCanHeadline}
                          </h3>
                          <p className="text-sm text-gray-600 leading-relaxed" data-id="element-1185" data-mp="55mdcct1vif0">
                            {template.financialAid.weCanBody}
                          </p>
                        </div>}
                      {template.financialAid.exploreHeadline && <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm" data-id="element-1186" data-mp="1397zpi7tmir">
                          <h3 className="font-bold text-gray-900 mb-2" data-id="element-1187" data-mp="2k74bmka1wfk7">
                            {template.financialAid.exploreHeadline}
                          </h3>
                          <p className="text-sm text-gray-600 leading-relaxed" data-id="element-1188" data-mp="2t70sw0u42c58">
                            {template.financialAid.exploreBody}
                          </p>
                        </div>}
                    </div>}
                </div> : <p className="text-gray-600 text-base md:text-lg leading-relaxed mb-8 max-w-xl mx-auto lg:mx-0" data-id="element-1189" data-mp="376jwqhfw31bq">
                  {template.subhead}
                </p>}

              {/* Stats strip — site theme */}
            </div>

            {/* RIGHT: Form — appears ABOVE the supporting text on mobile */}
            <aside className="w-full max-w-md mx-auto lg:max-w-none lg:mx-0 order-1 lg:order-2" data-id="element-1190" data-mp="3lwrbon6ou27o">
              {/* Step indicator */}
              <div className="flex items-center gap-3 mb-5 px-1" data-id="element-1191" data-mp="3qsoquq67t2iv">
                <div className="flex items-center gap-2" data-id="element-1192" data-mp="1f87e79kzwf76">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-white" style={{
                  backgroundColor: ORANGE
                }} data-id="element-1193" data-mp="1a8meuy1wy4tx">
                    <CheckIcon className="w-4 h-4" data-id="element-1194" data-mp="10bgmlmhkc7s4" />
                  </div>
                  <span className="text-xs font-semibold text-gray-700" data-id="element-1195" data-mp="1xy6y7i1zb43q">
                    Profile Complete
                  </span>
                </div>
                <div className="flex-grow h-px bg-gray-300" data-id="element-1196" data-mp="1t0xaq7y5h2ij"></div>
                <div className="flex items-center gap-2" data-id="element-1197" data-mp="1o6t2zmh28qge">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold animate-pulse" style={{
                  backgroundColor: NAVY,
                  boxShadow: `0 0 0 4px rgba(232,89,79,0.15)`
                }} data-id="element-1198" data-mp="1j9arbobnrx4p">
                    2
                  </div>
                  <span className="text-xs font-semibold" style={{
                  color: NAVY
                }} data-id="element-1199" data-mp="a2io4grvzmvm">
                    Confirm Email
                  </span>
                </div>
              </div>

              {isSelector ? <form onSubmit={handleSelectorSubmit} className="bg-white rounded-2xl p-7 md:p-9 shadow-xl border border-gray-200" data-id="element-1200" data-mp="3nfatozr0yf40">
                  <div className="text-center mb-6" data-id="element-1201" data-mp="3dgv4517nbdwm">
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1" data-id="element-1202" data-mp="3di6ghpf5ptzc">
                      {template.cardTitle}
                    </h2>
                    <p className="text-sm text-gray-600" data-id="element-1203" data-mp="1bprry7i3xr9y">
                      {template.cardSubtitle}
                    </p>
                  </div>

                  <div className="space-y-5" data-id="element-1204" data-mp="1go33qnryqbut">
                    <div data-id="element-1205" data-mp="316dup2jp9672">
                      <label className="block text-sm font-bold mb-2 text-gray-900" data-id="element-1206" data-mp="317p0f7wpgo0t">
                        Degree Level <span className="text-red-500" data-id="element-1207" data-mp="2w8lttj12u9qq">*</span>
                      </label>
                      <select value={degreeLevel} onChange={e => setDegreeLevel(e.target.value ? Number(e.target.value) : '')} required className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 bg-white text-gray-900 focus:outline-none focus:border-[#e8594f] focus:ring-2 focus:ring-[#e8594f]/20 hover:border-gray-300 transition-colors appearance-none cursor-pointer" style={{
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                    backgroundPosition: 'right 0.75rem center',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: '1.25em 1.25em',
                    paddingRight: '2.5rem'
                  }} data-id="element-1208" data-mp="3awoz0q7apheh">
                        <option value="" data-id="element-1209" data-mp="2uqzrharsiib">-- Please Select --</option>
                        {degreeLevelOptions.filter(o => o.value !== 5).map(option => <option key={option.value} value={option.value} data-id="element-1210" data-mp="buxf78q13sk4">
                              {option.label}
                            </option>)}
                      </select>
                    </div>

                    <div data-id="element-1211" data-mp="3g9ezv0aw7mkj">
                      <label className="block text-sm font-bold mb-2 text-gray-900" data-id="element-1212" data-mp="3g7uea06k3dyt">
                        Area of Study <span className="text-red-500" data-id="element-1213" data-mp="3k8wa0719l7w1">*</span>
                      </label>
                      <select value={areaOfStudy} onChange={e => {
                    const v = e.target.value ? Number(e.target.value) : '';
                    setAreaOfStudy(v);
                    setProgram('');
                  }} required className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 bg-white text-gray-900 focus:outline-none focus:border-[#e8594f] focus:ring-2 focus:ring-[#e8594f]/20 hover:border-gray-300 transition-colors appearance-none cursor-pointer" style={{
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                    backgroundPosition: 'right 0.75rem center',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: '1.25em 1.25em',
                    paddingRight: '2.5rem'
                  }} data-id="element-1214" data-mp="3cpdifcdyg6bu">
                        <option value="" data-id="element-1215" data-mp="1mjvkd0qnmw3y">-- Please Select --</option>
                        {areaStudyOptions.map(option => <option key={option.value} value={option.value} data-id="element-1216" data-mp="2f5n1urcvk2l5">
                            {option.label}
                          </option>)}
                      </select>
                    </div>

                    <div data-id="element-1217" data-mp="25flksjixvkfq">
                      <label className="block text-sm font-bold mb-2 text-gray-900" data-id="element-1218" data-mp="25eakitopcutd">
                        Program <span className="text-red-500" data-id="element-1219" data-mp="2addftmevrmo5">*</span>
                      </label>
                      <select value={program} onChange={e => setProgram(e.target.value ? Number(e.target.value) : '')} required disabled={!areaOfStudy} className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 bg-white text-gray-900 focus:outline-none focus:border-[#e8594f] focus:ring-2 focus:ring-[#e8594f]/20 hover:border-gray-300 transition-colors appearance-none cursor-pointer disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed" style={{
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                    backgroundPosition: 'right 0.75rem center',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: '1.25em 1.25em',
                    paddingRight: '2.5rem'
                  }} data-id="element-1220" data-mp="2y3px3nw0mvre">
                        <option value="" data-id="element-1221" data-mp="3nehnst36edgl">
                          {areaOfStudy ? '-- Please Select --' : 'Select an area of study first'}
                        </option>
                        {availablePrograms.map(option => <option key={option.value} value={option.value} data-id="element-1222" data-mp="4z71lwtleo3k">
                            {option.label}
                          </option>)}
                      </select>
                    </div>

                    <button type="submit" disabled={submitting || !degreeLevel || !areaOfStudy || !program} className="w-full flex items-center justify-center gap-2 text-white font-bold py-4 px-6 rounded-lg shadow-lg transition-all hover:shadow-xl active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed" style={{
                  backgroundColor: ORANGE
                }} onMouseEnter={e => {
                  if (!(e.currentTarget as HTMLButtonElement).disabled) {
                    ;
                    (e.currentTarget as HTMLButtonElement).style.backgroundColor = ORANGE_HOVER;
                  }
                }} onMouseLeave={e => {
                  ;
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = ORANGE;
                }} data-id="element-1223" data-mp="ew3hinltmci5">
                      <span data-id="element-1224" data-mp="1cip8961af72">{submitting ? 'Loading…' : template.ctaLabel}</span>
                      <ArrowRightIcon className="w-4 h-4" data-id="element-1225" data-mp="1afoov7993o3" />
                    </button>

                    <p className="text-center text-xs text-gray-500 flex items-center justify-center gap-1.5" data-id="element-1226" data-mp="3shifvmn3f9yb">
                      <LockIcon className="w-3.5 h-3.5" data-id="element-1227" data-mp="ymypfcuo8qdq" />
                      Your information is secure and confidential
                    </p>
                  </div>
                </form> : isCta ? <div className="bg-white rounded-2xl p-8 md:p-10 shadow-xl border border-gray-200 text-center" data-id="element-1228" data-mp="jrsjepvksrke">
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3" data-id="element-1229" data-mp="fqp4q6p9l1kr">
                    {template.cardHeadline || template.cardTitle}
                  </h2>
                  <p className="text-base font-semibold mb-7" style={{
                color: ORANGE
              }} data-id="element-1230" data-mp="axm6hl9jl3y8">
                    {template.cardSubcopy || template.cardSubtitle}
                  </p>

                  <button type="button" onClick={handleCtaClick} disabled={submitting} className="inline-flex items-center justify-center gap-2 text-white font-bold py-4 px-10 rounded-lg shadow-lg transition-all hover:shadow-xl active:scale-[0.99] disabled:opacity-70" style={{
                backgroundColor: ORANGE
              }} onMouseEnter={e => {
                ;
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = ORANGE_HOVER;
              }} onMouseLeave={e => {
                ;
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = ORANGE;
              }} data-id="element-1231" data-mp="3s6stk4uv1fju">
                    <span data-id="element-1232" data-mp="1yqg35ez1g0mf">{submitting ? 'Loading…' : template.ctaLabel}</span>
                    <ArrowRightIcon className="w-4 h-4" data-id="element-1233" data-mp="118g0nubxw6q5" />
                  </button>

                  {template.cardFootnote && <p className="text-sm text-gray-600 mt-7 leading-relaxed max-w-sm mx-auto" data-id="element-1234" data-mp="166hfu468vlwc">
                      {template.cardFootnote}
                    </p>}
                </div> : <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-7 md:p-9 shadow-xl border border-gray-200" data-id="element-1235" data-mp="17xo5u41aeal1">
                  <label htmlFor="bridge-email" className="block text-sm font-bold mb-3 text-gray-900" data-id="element-1236" data-mp="130o0jvc9hbuz">
                    Where should we send your matches?
                  </label>

                  <div className="relative mb-4" data-id="element-1237" data-mp="p7ga46zx2mev">
                    <MailIcon className="w-5 h-5 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2" data-id="element-1238" data-mp="p53jz6k095qq" />
                    <input id="bridge-email" type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="yourname@example.com" className="w-full pl-12 pr-4 py-3.5 rounded-lg border-2 border-gray-200 bg-white text-base text-gray-900 focus:outline-none focus:border-[#e8594f] focus:ring-2 focus:ring-[#e8594f]/20 hover:border-gray-300 transition-colors" data-id="element-1239" data-mp="kag4fx6zbloa" />
                  </div>

                  <button type="submit" disabled={submitting} className="w-full flex items-center justify-center gap-2 text-white font-bold py-4 px-6 rounded-lg shadow-lg transition-all hover:shadow-xl active:scale-[0.99] disabled:opacity-70" style={{
                backgroundColor: ORANGE
              }} onMouseEnter={e => {
                ;
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = ORANGE_HOVER;
              }} onMouseLeave={e => {
                ;
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = ORANGE;
              }} data-id="element-1240" data-mp="39jjtepgttgmk">
                    <span data-id="element-1241" data-mp="2nbfrqajab11f">{submitting ? 'Loading…' : template.ctaLabel}</span>
                    <ArrowRightIcon className="w-4 h-4" data-id="element-1242" data-mp="2icurib418hcz" />
                  </button>

                  {/* Inline trust row */}
                  <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mt-5 text-xs text-gray-600" data-id="element-1243" data-mp="3f3dhll5x8292">
                    <div className="flex items-center gap-1.5" data-id="element-1244" data-mp="3b2tjofdiwulv">
                      <ShieldCheckIcon className="w-3.5 h-3.5 text-green-500" data-id="element-1245" data-mp="36aa81nyec9jp" />
                      <span data-id="element-1246" data-mp="31bqkxk82grpr">No credit card</span>
                    </div>
                    <div className="flex items-center gap-1.5" data-id="element-1247" data-mp="31bgvbxqxomkl">
                      <ZapIcon className="w-3.5 h-3.5 text-green-500" data-id="element-1248" data-mp="2ms2d70cg49z" />
                      <span data-id="element-1249" data-mp="3u0o0vgot2cfc">Instant results</span>
                    </div>
                    <div className="flex items-center gap-1.5" data-id="element-1250" data-mp="3tyjoeacuc53g">
                      <CheckIcon className="w-3.5 h-3.5 text-green-500" data-id="element-1251" data-mp="57g8st27r56w" />
                      <span data-id="element-1252" data-mp="a4g6afkzyzme">No obligation</span>
                    </div>
                  </div>

                  <p className="text-[11px] text-gray-500 mt-5 leading-relaxed text-center" data-id="element-1253" data-mp="f42v7ma6u4b1">
                    By clicking, you agree to our{' '}
                    <a href="/terms" className="underline text-gray-700 hover:text-gray-900" data-id="element-1254" data-mp="j3tgxbkuxu8p">
                      Terms
                    </a>{' '}
                    &{' '}
                    <a href="/privacy-policy" className="underline text-gray-700 hover:text-gray-900" data-id="element-1255" data-mp="xuaiqw1emm64">
                      Privacy Policy
                    </a>{' '}
                    and consent to receive program info via email, phone, or
                    text.
                  </p>
                </form>}
            </aside>
          </div>
        </div>
      </section>

      {/* Content Section — How it works + Testimonials + Trust */}
      <section className="w-full bg-white border-t border-gray-200" data-id="element-1256" data-mp="33xfu9zu7jmle">
        <div className="max-w-7xl mx-auto px-4 py-16 md:py-20" data-id="element-1257" data-mp="3ho2ujwfm9kjl">
          {/* How It Works */}
          <div className="text-center mb-12" data-id="element-1258" data-mp="3cx2u4h7agkcc">
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full mb-4 text-[11px] font-bold tracking-widest" style={{
            backgroundColor: 'rgba(232,89,79,0.1)',
            color: ORANGE
          }} data-id="element-1259" data-mp="3cv9rre3pkma5">
              <span className="w-1.5 h-1.5 rounded-full" style={{
              backgroundColor: ORANGE
            }} data-id="element-1260" data-mp="4fq18muf9hys"></span>
              HOW IT WORKS
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3" data-id="element-1261" data-mp="26agtpe0e9ui8">
              Your Path to the Right Program in 3 Steps
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-1262" data-mp="gqz53t08bhd1">
              We've helped thousands of students find accredited online programs
              that fit their goals, schedule, and budget.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-16" data-id="element-1263" data-mp="7yfar4qyzyy0">
            {[{
            icon: ClipboardListIcon,
            step: '01',
            title: 'Tell Us About You',
            body: 'Share your goals, education level, and area of interest in under a minute.'
          }, {
            icon: SparklesIcon,
            step: '02',
            title: 'Get Matched Instantly',
            body: 'Our system pairs you with accredited online programs that fit your profile.'
          }, {
            icon: SchoolIcon,
            step: '03',
            title: 'Connect With Schools',
            body: 'Compare programs side-by-side and connect directly with admissions teams.'
          }].map((s, i) => {
            const Icon = s.icon;
            return <div key={i} className="relative bg-gray-50 rounded-2xl p-7 border border-gray-200 hover:border-[#e8594f]/40 hover:shadow-md transition-all" data-id="element-1264" data-mp="67r8uloh40c1" data-mpi={i}>
                  <div className="flex items-center justify-between mb-5" data-id="element-1265" data-mp="3skr66pjyx0fo">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{
                  backgroundColor: 'rgba(232,89,79,0.1)'
                }} data-id="element-1266" data-mp="3om05k8gan0nt">
                      <Icon className="w-6 h-6" style={{
                    color: ORANGE
                  }} data-id="element-1267" data-mp="39o7tjq9ygued" />
                    </div>
                    <span className="text-3xl font-bold text-gray-200" data-id="element-1268" data-mp="17wcwafog4sfo">
                      {s.step}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-1269" data-mp="1gbsntqgd0f2y">
                    {s.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed" data-id="element-1270" data-mp="1p6733t0w3pm2">
                    {s.body}
                  </p>
                </div>;
          })}
          </div>

          {/* Trust band */}
          <div className="mt-16 rounded-2xl p-8 md:p-10 relative overflow-hidden" style={{
          backgroundColor: NAVY
        }} data-id="element-1271" data-mp="xfj2qj71io9g">
            <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#2a374a] to-transparent opacity-50 pointer-events-none" data-id="element-1272" data-mp="16fpadog24u0p"></div>
            <div className="relative z-10 grid md:grid-cols-3 gap-6 items-center" data-id="element-1273" data-mp="1bg3kdc17ye1d">
              <div className="flex items-start gap-3" data-id="element-1274" data-mp="2htt36rawsc46">
                <AwardIcon className="w-7 h-7 flex-shrink-0 mt-0.5" style={{
                color: ORANGE
              }} data-id="element-1275" data-mp="2mtpbh2qn2ppj" />
                <div data-id="element-1276" data-mp="s6kogeh81z05">
                  <div className="font-bold text-white mb-1" data-id="element-1277" data-mp="w6bkawllv5bj">
                    Only Accredited Schools
                  </div>
                  <div className="text-xs text-gray-300 leading-relaxed" data-id="element-1278" data-mp="idlx4oucbucu">
                    Every partner school is regionally or nationally accredited.
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-1279" data-mp="8ewdaqivvzty">
                <ShieldCheckIcon className="w-7 h-7 flex-shrink-0 mt-0.5" style={{
                color: ORANGE
              }} data-id="element-1280" data-mp="dftxf8ngf3y3" />
                <div data-id="element-1281" data-mp="4e42vty9zow3">
                  <div className="font-bold text-white mb-1" data-id="element-1282" data-mp="4fnjxmrra3yx">
                    Your Privacy Protected
                  </div>
                  <div className="text-xs text-gray-300 leading-relaxed" data-id="element-1283" data-mp="1tubudigsdbub">
                    SSL encrypted. We never sell your data to unrelated third
                    parties.
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-1284" data-mp="23y0qbya16jno">
                <ZapIcon className="w-7 h-7 flex-shrink-0 mt-0.5" style={{
                color: ORANGE
              }} data-id="element-1285" data-mp="23zbqzp86jbak" />
                <div data-id="element-1286" data-mp="2cwm7pq3rnsmv">
                  <div className="font-bold text-white mb-1" data-id="element-1287" data-mp="2cw2q6ai1369d">
                    Here When You Need Us
                  </div>
                  <div className="text-xs text-gray-300 leading-relaxed" data-id="element-1288" data-mp="2mo8mfwfbc9ud">
                    Explore programs and compare your options at your pace.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="w-full border-t border-gray-200 bg-white" data-id="element-1289" data-mp="169gufkm2x4ll">
        <div className="max-w-7xl mx-auto px-4 py-8 text-center text-gray-700" data-id="element-1290" data-mp="16btbzwnxdcy4">
          <nav className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs mb-4" data-id="element-1291" data-mp="129pyl2hp3ya7">
            <a href="/privacy-policy" className="hover:text-[#e8594f] transition-colors" data-id="element-1292" data-mp="31egbvjqbwfch">
              Privacy Policy
            </a>
            <a href="/terms" className="hover:text-[#e8594f] transition-colors" data-id="element-1293" data-mp="3ba1dvhvedfm8">
              Terms of Use
            </a>
            <a href="/ca-notice" className="hover:text-[#e8594f] transition-colors" data-id="element-1294" data-mp="3gbh2fjwsa49d">
              CA Privacy Rights
            </a>
            <a href="/cookie-policy" className="hover:text-[#e8594f] transition-colors" data-id="element-1295" data-mp="3p9srhhi04tzp">
              Cookies
            </a>
          </nav>
          <p className="text-xs text-gray-600 mb-1" data-id="element-1296" data-mp="2vj2p7a5tjye">
            © {new Date().getFullYear()} FindOnlineDegrees.org · Media Matchers
            LLC · All Rights Reserved.
          </p>
          <p className="text-[11px] text-gray-500 max-w-2xl mx-auto leading-relaxed" data-id="element-1297" data-mp="bvo8xe3macaz">
            FindOnlineDegrees.org is a service that helps students explore
            accredited online degree programs. We may receive compensation from
            school partners when users inquire.
          </p>
        </div>
      </footer>
    </div>;
}